import { useEffect, useRef, useState, type CSSProperties, type FormEvent } from 'react'
import type {
  ArticleSummary,
  HeadlineBlock,
  MostReadBlock,
  NewsletterBlock,
  SectionBlock,
} from '@publiqa/shared'
import { ApiError, post } from '../../lib/api'
import { formatDate } from '../../lib/richtext'
import { useFetch } from '../../lib/useFetch'
import { SmartLink, usePublic, usePubliqApi } from '../context'
import { Picture } from '../ui'

/**
 * Blocs du JOURNAL : la une, les rubriques, les plus lus, la lettre
 * d'information. Mise en page inspirée des vitrines modernes (unowhy) :
 * grands visuels arrondis, diaporama, tuiles photo, bandeaux défilants.
 */

type Params = Record<string, string | number | undefined>

export function useArticles(params: Params) {
  const path = usePubliqApi()
  return useFetch<{ articles: ArticleSummary[]; total: number }>(path('/articles', params))
}

const href = (a: ArticleSummary) => `/article/${a.slug}`

/** Pastille de rubrique, à sa couleur. */
export function Pill({ article, onImage = false }: { article: ArticleSummary; onImage?: boolean }) {
  const label = article.category?.name ?? article.kicker
  if (!label) return null
  return (
    <span
      className={`mg-pill${onImage ? ' on-image' : ''}`}
      style={{ '--c': article.category?.color ?? 'var(--accent)' } as CSSProperties}
    >
      {label}
    </span>
  )
}

/** Mention « Abonnés » des articles réservés. */
export function Premium({ article }: { article: ArticleSummary }) {
  if (article.access !== 'subscribers') return null
  return (
    <span className="mg-premium" title="Réservé aux abonnés">
      <svg viewBox="0 0 16 16" aria-hidden="true">
        <path d="M8 1.5 9.9 5.4l4.3.6-3.1 3 .7 4.3L8 11.3l-3.8 2 .7-4.3-3.1-3 4.3-.6z" />
      </svg>
      Abonnés
    </span>
  )
}

function Meta({ article, author = true }: { article: ArticleSummary; author?: boolean }) {
  return (
    <p className="mg-meta">
      {article.status !== 'published' && <span className="badge-draft">Brouillon</span>}
      {author && article.author && <span>{article.author}</span>}
      <time dateTime={article.publishedAt ?? undefined}>{formatDate(article.publishedAt)}</time>
      <span>{article.readingMinutes} min</span>
    </p>
  )
}

/**
 * Carte d'article. `tile` : photo arrondie, titre dessous (vitrine) ; `cover` :
 * titre en surimpression sur la photo ; `side` : vignette à gauche ; `text` :
 * sans image.
 */
export function Card({
  article,
  variant = 'tile',
  eager = false,
}: {
  article: ArticleSummary
  variant?: 'tile' | 'cover' | 'side' | 'text'
  eager?: boolean
}) {
  if (variant === 'cover') {
    return (
      <article className="mg-card mg-card-cover">
        <Picture image={article.cover} alt={article.title} eager={eager} />
        <div className="mg-card-shade" aria-hidden="true" />
        <div className="mg-card-body">
          <div className="mg-card-tags">
            <Pill article={article} onImage />
            <Premium article={article} />
          </div>
          <h3>
            <SmartLink href={href(article)} className="mg-stretch">
              {article.title}
            </SmartLink>
          </h3>
          <Meta article={article} author={false} />
        </div>
      </article>
    )
  }
  return (
    <article className={`mg-card mg-card-${variant}`}>
      {variant !== 'text' && (
        <div className="mg-card-media">
          <Picture image={article.cover} alt={article.title} eager={eager} />
        </div>
      )}
      <div className="mg-card-body">
        <div className="mg-card-tags">
          <Pill article={article} />
          <Premium article={article} />
        </div>
        <h3>
          <SmartLink href={href(article)} className="mg-stretch">
            {article.title}
          </SmartLink>
        </h3>
        {variant !== 'side' && article.excerpt && <p className="mg-excerpt">{article.excerpt}</p>}
        <Meta article={article} author={variant !== 'side'} />
      </div>
    </article>
  )
}

// ──────────────────────────────────────────────────────────── la une

/**
 * Repère temporel du fil : l'heure pour un article du jour, « Hier » pour la
 * veille, sinon la date (avec l'année si ce n'est pas l'année en cours). La
 * date complète reste dans l'attribut `title` et `dateTime`.
 */
export function feedStamp(iso: string | null, now = new Date()): string {
  if (!iso) return '—'
  const d = new Date(iso)
  const day = (x: Date) => x.toLocaleDateString('fr-CA', { timeZone: 'Europe/Paris' })
  if (day(d) === day(now))
    return d.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Paris',
    })
  if (day(d) === day(new Date(now.getTime() - 86_400_000))) return 'Hier'
  const sameYear = d.getFullYear() === now.getFullYear()
  return d.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: '2-digit' }),
    timeZone: 'Europe/Paris',
  })
}

function Feed({ title }: { title: string }) {
  const { data } = useArticles({ limit: 8 })
  const items = data?.articles ?? []
  return (
    <aside className="mg-fil" aria-label={title}>
      <h2>
        <span className="mg-live" aria-hidden="true" /> {title}
      </h2>
      <ol>
        {items.map((a) => (
          <li key={a.id}>
            <time
              dateTime={a.publishedAt ?? undefined}
              title={a.publishedAt ? formatDate(a.publishedAt, true) : undefined}
            >
              {feedStamp(a.publishedAt)}
            </time>
            <SmartLink href={href(a)}>{a.title}</SmartLink>
          </li>
        ))}
      </ol>
      <SmartLink href="/actualites" className="mg-more">
        Tout le fil <span aria-hidden="true">→</span>
      </SmartLink>
    </aside>
  )
}

/** Diaporama des articles à la une : fondu, pause au survol, barres de progression. */
function Slider({ articles, interval }: { articles: ArticleSummary[]; interval: number }) {
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  const count = articles.length
  useEffect(() => {
    if (paused || count < 2) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const t = window.setTimeout(() => setIndex((i) => (i + 1) % count), interval * 1000)
    return () => window.clearTimeout(t)
  }, [paused, count, interval, index])
  const go = (d: number) => setIndex((i) => (i + d + count) % count)

  return (
    <div
      className={`mg-slider${paused ? ' paused' : ''}`}
      role="region"
      aria-roledescription="diaporama"
      aria-label="À la une"
      style={{ '--interval': `${interval}s` } as CSSProperties}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      {articles.map((a, i) => (
        <article
          key={a.id}
          className={`mg-slide${i === index ? ' on' : ''}`}
          aria-hidden={i !== index}
          aria-roledescription="diapositive"
          aria-label={`${i + 1} sur ${count}`}
        >
          <Picture image={a.cover} alt={a.title} eager={i === 0} />
          <div className="mg-slide-shade" aria-hidden="true" />
          <div className="mg-slide-body">
            <div className="mg-card-tags">
              <Pill article={a} onImage />
              <Premium article={a} />
            </div>
            <h2>
              <SmartLink href={href(a)} className="mg-stretch" tabIndex={i === index ? 0 : -1}>
                {a.title}
              </SmartLink>
            </h2>
            {a.excerpt && <p>{a.excerpt}</p>}
          </div>
        </article>
      ))}
      {count > 1 && (
        <div className="mg-slider-nav">
          <div className="mg-bars">
            {articles.map((a, i) => (
              <button
                type="button"
                key={a.id}
                className={i === index ? 'on' : i < index ? 'done' : ''}
                onClick={() => setIndex(i)}
                aria-label={`Article ${i + 1} : ${a.title}`}
                aria-current={i === index}
              >
                <span />
              </button>
            ))}
          </div>
          <div className="mg-arrows">
            <button type="button" onClick={() => go(-1)} aria-label="Article précédent">
              <Chevron dir="left" />
            </button>
            <button type="button" onClick={() => go(1)} aria-label="Article suivant">
              <Chevron dir="right" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export function Chevron({ dir }: { dir: 'left' | 'right' }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d={dir === 'left' ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7'} />
    </svg>
  )
}

export function HeadlineView({ b }: { b: HeadlineBlock }) {
  const { data, loading } = useArticles({
    limit: b.layout === 'slider' ? b.count : Math.max(b.count, 5),
    featuredFirst: b.source === 'featured' ? 1 : undefined,
  })
  const items = data?.articles ?? []
  if (loading && !data) return <div className="container mg-une-skeleton" aria-busy="true" />
  if (!items.length)
    return (
      <section className="container mg-empty">
        <p>Aucun article publié pour le moment.</p>
      </section>
    )
  const [lead, ...rest] = items
  return (
    <section className={`container mg-une mg-une-${b.layout}${b.showFeed ? ' with-feed' : ''}`}>
      <div className="mg-une-main">
        {b.layout === 'slider' && <Slider articles={items} interval={b.interval} />}
        {b.layout === 'classic' && (
          <div className="mg-classic">
            <Card article={lead!} variant="tile" eager />
            <div className="mg-classic-side">
              {rest.slice(0, 4).map((a) => (
                <Card key={a.id} article={a} variant="side" />
              ))}
            </div>
          </div>
        )}
        {b.layout === 'mosaic' && (
          <div className="mg-mosaic">
            <Card article={lead!} variant="cover" eager />
            {rest.slice(0, 4).map((a) => (
              <Card key={a.id} article={a} variant="cover" />
            ))}
          </div>
        )}
      </div>
      {b.showFeed && <Feed title={b.feedTitle || 'Le fil'} />}
    </section>
  )
}

// ─────────────────────────────────────────────────────────── rubrique

/** Bandeau défilant (scroll-snap), flèches comme les carrousels de vitrine. */
export function Row({ articles }: { articles: ArticleSummary[] }) {
  const track = useRef<HTMLDivElement>(null)
  const [edges, setEdges] = useState({ start: true, end: false })
  const update = () => {
    const el = track.current
    if (!el) return
    setEdges({
      start: el.scrollLeft < 8,
      end: el.scrollLeft + el.clientWidth > el.scrollWidth - 8,
    })
  }
  useEffect(update, [articles])
  const scroll = (d: number) => {
    const el = track.current
    if (el) el.scrollBy({ left: d * el.clientWidth * 0.85, behavior: 'smooth' })
  }
  return (
    <div className="mg-row">
      <div className="mg-row-track" ref={track} onScroll={update}>
        {articles.map((a) => (
          <Card key={a.id} article={a} variant="tile" />
        ))}
      </div>
      <div className="mg-arrows mg-row-arrows">
        <button
          type="button"
          onClick={() => scroll(-1)}
          disabled={edges.start}
          aria-label="Précédents"
        >
          <Chevron dir="left" />
        </button>
        <button type="button" onClick={() => scroll(1)} disabled={edges.end} aria-label="Suivants">
          <Chevron dir="right" />
        </button>
      </div>
    </div>
  )
}

export function SectionView({ b }: { b: SectionBlock }) {
  const { categories } = usePublic()
  const category = categories.find((c) => c.slug === b.category)
  const { data } = useArticles({ category: b.category || undefined, limit: b.count })
  const items = data?.articles ?? []
  if (data && !items.length) return null
  const title = b.title || category?.name || 'À lire'
  const more = category ? `/rubrique/${category.slug}` : '/actualites'
  const [lead, ...rest] = items
  // Grande tuile seulement si elle complète la rangée (3, 7, 11… articles sur 4 colonnes).
  const wide = items.length % 4 === 3
  return (
    <section
      className={`container mg-section mg-section-${b.layout}`}
      style={{ '--c': category?.color ?? 'var(--accent)' } as CSSProperties}
    >
      <header className="mg-section-head">
        <h2>
          <SmartLink href={more}>{title}</SmartLink>
        </h2>
        {b.moreLabel && (
          <SmartLink href={more} className="mg-more">
            {b.moreLabel} <span aria-hidden="true">→</span>
          </SmartLink>
        )}
      </header>
      {b.layout === 'tiles' && (
        <div className={`mg-tiles n-${Math.min(items.length, 4)}`}>
          {items.map((a, i) => (
            <Card key={a.id} article={a} variant={i === 0 && wide ? 'cover' : 'tile'} />
          ))}
        </div>
      )}
      {b.layout === 'row' && <Row articles={items} />}
      {b.layout === 'lead' && lead && (
        <div className="mg-lead">
          <Card article={lead} variant="cover" />
          <div className="mg-lead-side">
            {rest.map((a) => (
              <Card key={a.id} article={a} variant="side" />
            ))}
          </div>
        </div>
      )}
      {b.layout === 'list' && (
        <div className="mg-list">
          {items.map((a) => (
            <Card key={a.id} article={a} variant="text" />
          ))}
        </div>
      )}
    </section>
  )
}

// ──────────────────────────────────────────────────────── les plus lus

export function MostReadView({ b }: { b: MostReadBlock }) {
  const { data } = useArticles({ sort: 'popular', days: b.days, limit: b.count })
  const items = data?.articles ?? []
  if (!items.length) return null
  return (
    <section className="container mg-mostread">
      <header className="mg-section-head">
        <h2>{b.title || 'Les plus lus'}</h2>
      </header>
      <ol>
        {items.map((a, i) => (
          <li key={a.id}>
            <span className="mg-rank" aria-hidden="true">
              {i + 1}
            </span>
            <div>
              <Pill article={a} />
              <SmartLink href={href(a)} className="mg-stretch">
                {a.title}
              </SmartLink>
            </div>
            <div className="mg-mostread-thumb">
              <Picture image={a.cover} alt={a.title} />
            </div>
          </li>
        ))}
      </ol>
    </section>
  )
}

// ──────────────────────────────────────────────── lettre d'information

/** Formulaire d'inscription à la lettre (double confirmation par e-mail). */
export function SubscribeForm({
  buttonLabel = 'Je m’inscris',
  successMessage = 'Merci ! Confirmez votre inscription grâce au lien que nous venons de vous envoyer.',
  askName = false,
  compact = false,
}: {
  buttonLabel?: string
  successMessage?: string
  askName?: boolean
  compact?: boolean
}) {
  const { publication, inert } = usePublic()
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [consent, setConsent] = useState(false)
  const [state, setState] = useState<'idle' | 'busy' | 'done'>('idle')
  const [error, setError] = useState('')
  const hp = useRef<HTMLInputElement>(null)
  // Temps passé sur le formulaire : distingue une personne d'un robot (voir l'API).
  const shownAt = useRef(Date.now())

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (inert) return
    setState('busy')
    setError('')
    try {
      await post(`/public/p/${publication.slug}/subscribe`, {
        email,
        name,
        consent,
        _hp: hp.current?.value ?? '',
        _t: Date.now() - shownAt.current,
      })
      setState('done')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Inscription impossible pour le moment.')
      setState('idle')
    }
  }

  if (state === 'done')
    return (
      <p className="mg-subscribe-done" role="status">
        {successMessage}
      </p>
    )
  return (
    <form className={`mg-subscribe${compact ? ' compact' : ''}`} onSubmit={submit}>
      <div className="mg-subscribe-fields">
        {askName && (
          <input
            type="text"
            placeholder="Votre prénom"
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label="Prénom"
            autoComplete="given-name"
          />
        )}
        <input
          type="email"
          required
          placeholder="Votre adresse e-mail"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-label="Adresse e-mail"
          autoComplete="email"
        />
        <button type="submit" className="btn btn-primary" disabled={state === 'busy'}>
          {state === 'busy' ? 'Envoi…' : buttonLabel}
        </button>
      </div>
      <input
        ref={hp}
        type="text"
        name="_hp"
        className="hp"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />
      <label className="mg-consent">
        <input
          type="checkbox"
          required
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
        />
        <span>
          J’accepte de recevoir la lettre de {publication.name}. Désinscription en un clic, à tout
          moment.
        </span>
      </label>
      {error && (
        <p className="mg-error" role="alert">
          {error}
        </p>
      )}
    </form>
  )
}

export function NewsletterView({ b }: { b: NewsletterBlock }) {
  return (
    <section className="container">
      <div className={`mg-newsletter tone-${b.tone}`}>
        <div className="mg-newsletter-text">
          <span className="mg-newsletter-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <rect x="3" y="5" width="18" height="14" rx="3" />
              <path d="m4 7 8 6 8-6" />
            </svg>
          </span>
          {b.title && <h2>{b.title}</h2>}
          {b.text && <p>{b.text}</p>}
        </div>
        <SubscribeForm
          buttonLabel={b.buttonLabel}
          successMessage={b.successMessage}
          askName={b.askName}
        />
      </div>
    </section>
  )
}
